// BEGIN CUSTOM EDIT: Section-based collection navigation | Date: 2026-09-16 | What: Swap collection sections for form-grid links via the Section Rendering API
// Only the compatible collection template is replaced. Site chrome and app scripts stay mounted.
class CollectionNavigation {
  constructor() {
    this.main = document.getElementById("main");
    this.metadata = this.main?.querySelector("template[data-collection-navigation-meta]");
    if (window.Shopify?.designMode || this.metadata?.dataset.template !== "collection.standard-shoelaces") return;

    this.currentUrl = new URL(location.href);
    this.template = this.metadata.dataset.template;
    this.pending = null;
    this.status = document.createElement("p");
    this.status.className = "sr-only";
    this.status.setAttribute("role", "status");
    this.status.setAttribute("aria-live", "polite");
    this.main.append(this.status);
    this.markHistory();
    document.addEventListener("click", event => this.onClick(event));
    document.addEventListener("facet:update", event => {
      const id = event.detail?.url?.searchParams?.get("section_id");
      if (document.getElementById(`shopify-section-${id}`)?.classList.contains("shopify-section--main-collection")) this.cancel();
    }, { capture: true });
    document.addEventListener("facet:update:complete", event => {
      if (event.detail?.success) {
        this.currentUrl = new URL(location.href);
        this.markHistory();
      }
    });
    window.addEventListener("popstate", event => this.onPopState(event));
    // Fragment entries (skip link, in-page anchors) are stamped so history navigation can tell them apart.
    window.addEventListener("hashchange", () => this.markHistory());
    window.addEventListener("pagehide", () => this.cancel());
  }

  sections() {
    return Array.from(this.main.children).filter(element =>
      element.classList.contains("shopify-section") &&
      !Array.from(element.classList).some(name => name.startsWith("shopify-section-group-"))
    );
  }

  markHistory() {
    history.replaceState({
      ...history.state,
      collectionNavigation: { version: 1, template: this.template }
    }, "", location.href);
  }

  onClick(event) {
    const link = event.target.closest?.("a[href]");
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.hasAttribute("download") || (link.target && link.target !== "_self")) return;
    if (!link.matches(".collection-banner-editorial a.cbe-forms-grid__link")) {
      this.cancel();
      return;
    }
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.hash || !/\/collections\/[^/]+\/?$/.test(url.pathname)) {
      this.cancel();
      return;
    }
    for (const key of ["preview_theme_id", "pb"]) {
      if (!url.searchParams.has(key) && this.currentUrl.searchParams.has(key)) url.searchParams.set(key, this.currentUrl.searchParams.get(key));
    }
    url.searchParams.delete("page");
    event.preventDefault();
    if (this.key(url) === this.key(this.currentUrl) && !this.pending) return;
    this.navigate(url, { link });
  }

  key(url) {
    return url.origin + url.pathname + url.search;
  }

  onPopState(event) {
    const url = new URL(location.href);
    const state = event.state?.collectionNavigation;
    const sameDocument = this.key(url) === this.key(this.currentUrl);
    if (!state || state.version !== 1 || state.template !== this.template) {
      // An entry this router did not create: keep it when only the fragment differs, otherwise let the browser load it.
      if (sameDocument) {
        this.markHistory();
        return;
      }
      this.cancel();
      this.fallback(url, true);
      return;
    }
    if (sameDocument) {
      this.cancel();
      return;
    }
    this.navigate(url, { historyNavigation: true });
  }

  markLoading(request, link) {
    if (!link?.isConnected) return;
    const grid = link.closest(".cbe-forms-grid");
    request.loadingLink = link;
    request.previousCurrent = grid ? Array.from(grid.querySelectorAll('.cbe-forms-grid__link[aria-current="true"]')) : [];
    request.previousCurrent.forEach(current => {
      if (current === link) return;
      current.removeAttribute("aria-current");
      current.closest(".cbe-forms-grid__item")?.classList.remove("is-current");
    });
    link.setAttribute("aria-current", "true");
    link.closest(".cbe-forms-grid__item")?.classList.add("is-current");
    link.classList.add("cbe-is-loading");
  }

  clearLoading(request) {
    const link = request.loadingLink;
    if (!link) return;
    link.classList.remove("cbe-is-loading");
    if (request.committed || !link.isConnected) return;
    // The grid was not replaced: put the selection back where it was.
    link.removeAttribute("aria-current");
    link.closest(".cbe-forms-grid__item")?.classList.remove("is-current");
    request.previousCurrent.forEach(current => {
      current.setAttribute("aria-current", "true");
      current.closest(".cbe-forms-grid__item")?.classList.add("is-current");
    });
  }

  cancel() {
    const request = this.pending;
    if (!request) return;
    request.controller.abort();
    this.cancelCommittedRestoration(request);
    // A committed request already replaced the page; only its restoration was interrupted.
    this.finish(request, request.committed === true);
  }

  cancelCommittedRestoration(request) {
    if (!request.committed) return;
    const loader = this.main.querySelector("collection-infinite-scroll");
    loader?.cancelRestoration();
    loader?.abortCurrentRequest();
  }

  async navigate(url, { historyNavigation = false, link = null } = {}) {
    this.cancel();
    const sections = this.sections();
    const mainSection = sections.find(section => section.classList.contains("shopify-section--main-collection"));
    if (!mainSection || !sections.length) return this.fallback(url, historyNavigation);
    const request = {
      controller: new AbortController(), url, historyNavigation,
      sectionId: mainSection.id.replace("shopify-section-", ""),
      fromUrl: this.currentUrl.href, sections, timedOut: false
    };
    this.pending = request;
    this.markLoading(request, link);
    document.dispatchEvent(new CustomEvent("collection:navigation:start", { detail: {
      sectionId: request.sectionId, fromUrl: request.fromUrl, toUrl: url.href, historyNavigation
    } }));
    // The outgoing loader releases any restoration it owns in the start event.
    request.scrollRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
    this.main.setAttribute("aria-busy", "true");
    this.status.textContent = "";
    request.timeout = setTimeout(() => {
      request.timedOut = true;
      request.controller.abort();
      this.cancelCommittedRestoration(request);
    }, 20000);

    try {
      const documents = await this.fetchSections(request);
      this.assertCurrent(request);
      const incoming = sections.map(section => {
        const document = documents.get(section.id.replace("shopify-section-", ""));
        const element = document?.getElementById(section.id);
        if (!element?.classList.contains("shopify-section")) throw new Error("Collection section is missing");
        return element;
      });
      const newMain = incoming.find(section => section.id === mainSection.id);
      const metadata = newMain.querySelector("template[data-collection-navigation-meta]");
      this.validateMetadata(metadata, url);
      const breadcrumbs = metadata.content.querySelector(".breadcrumb-container");
      if (!breadcrumbs || !this.main.querySelector(":scope > .breadcrumb-container")) throw new Error("Collection breadcrumbs are missing");
      await this.prepareAssets(documents, request);
      this.assertCurrent(request);
      if (sections.some(section => !section.isConnected)) throw new Error("Collection changed during navigation");

      if (!historyNavigation) {
        const state = { ...history.state };
        delete state.collectionInfiniteScroll;
        state.collectionNavigation = { version: 1, template: this.template };
        history.pushState(state, "", url.href);
      }
      this.currentUrl = url;
      request.committed = true;
      this.main.setAttribute("data-collection-navigation-commit", "");
      sections.forEach((section, index) => section.replaceWith(document.importNode(incoming[index], true)));
      this.main.querySelector(":scope > .breadcrumb-container").replaceWith(document.importNode(breadcrumbs, true));
      this.metadata = this.main.querySelector("template[data-collection-navigation-meta]");
      this.updateHead(metadata);
      request.collection = {
        id: `gid://shopify/Collection/${metadata.dataset.collectionId}`,
        handle: metadata.dataset.handle,
        title: metadata.dataset.name,
        productsCount: Number(metadata.dataset.productsCount)
      };

      const loader = this.main.querySelector("collection-infinite-scroll");
      if (historyNavigation) await loader?.restoration;
      this.assertCurrent(request);
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      this.assertCurrent(request);
      if (!historyNavigation) {
        // The activated link left the DOM with its section; keep the focus position inside the grid.
        const selected = this.main.querySelector('.collection-banner-editorial .cbe-forms-grid__link[aria-current="true"]');
        selected?.focus({ preventScroll: true });
      }
      this.status.textContent = this.main.querySelector("h1")?.textContent.trim() || metadata.dataset.name;
      this.finish(request, true);
    } catch (error) {
      if (this.pending !== request) return;
      request.error = error.message;
      this.finish(request, false);
      if (!request.controller.signal.aborted || request.timedOut) this.fallback(url, historyNavigation);
    }
  }

  assertCurrent(request) {
    if (this.pending !== request || request.controller.signal.aborted) throw new DOMException("Collection navigation cancelled", "AbortError");
  }

  validateMetadata(metadata, url) {
    const data = metadata?.dataset;
    if (!data || data.template !== this.template || data.locale !== document.documentElement.lang || !data.collectionId || !data.title) {
      throw new Error("Destination collection template is incompatible");
    }
    const expectedPath = url.pathname.replace(/\/$/, "");
    const collectionUrl = new URL(data.path, location.origin);
    if (collectionUrl.origin !== url.origin || collectionUrl.pathname.replace(/\/$/, "") !== expectedPath) {
      throw new Error("Section response belongs to another collection");
    }
  }

  async fetchSections(request) {
    const ids = request.sections.map(section => section.id.replace("shopify-section-", ""));
    const documents = new Map();
    // Shopify accepts up to five section IDs per request. Normal form collections use three.
    for (let offset = 0; offset < ids.length; offset += 5) {
      const batch = ids.slice(offset, offset + 5);
      const url = new URL(request.url);
      url.searchParams.delete("section_id");
      url.searchParams.delete("sections[]");
      url.searchParams.set("sections", batch.join(","));
      // Do not send Accept: application/json: Shopify can negotiate its collection JSON endpoint.
      const response = await fetch(url, { credentials: "same-origin", signal: request.controller.signal });
      this.assertCurrent(request);
      const responseUrl = new URL(response.url);
      if (!response.ok || responseUrl.origin !== url.origin || responseUrl.pathname !== url.pathname) throw new Error("Collection section request failed");
      const payload = await response.json();
      this.assertCurrent(request);
      for (const id of batch) {
        if (typeof payload[id] !== "string" || !payload[id].trim()) throw new Error("Shopify returned an unavailable section");
        documents.set(id, new DOMParser().parseFromString(payload[id], "text/html"));
      }
    }
    return documents;
  }

  async prepareAssets(documents, request) {
    const loads = [];
    for (const parsed of documents.values()) {
      parsed.querySelectorAll("style[data-section-stylesheet]").forEach(style => {
        // Move existing component CSS into the persistent head before its old section is removed.
        const inHead = Array.from(document.head.querySelectorAll("style[data-section-stylesheet]")).some(current => current.textContent === style.textContent);
        if (!inHead) document.head.append(document.importNode(style, true));
        style.remove();
      });
      parsed.querySelectorAll('link[rel="stylesheet"][href]').forEach(link => {
        const href = new URL(link.getAttribute("href"), request.url).href;
        let current = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).find(node => node.href === href);
        if (!current) {
          current = document.importNode(link, true);
          current.href = href;
          loads.push(this.waitForStylesheet(current, request.controller.signal));
          document.head.append(current);
        } else {
          if (!current.sheet) loads.push(this.waitForStylesheet(current, request.controller.signal));
          if (current.parentElement !== document.head) document.head.append(current);
        }
        link.remove();
      });
    }
    await Promise.all(loads);
    this.assertCurrent(request);
    if (!customElements.get("collection-infinite-scroll")) {
      const loaderDocument = Array.from(documents.values()).find(parsed => parsed.querySelector("collection-infinite-scroll"));
      const script = loaderDocument && Array.from(loaderDocument.querySelectorAll('script[type="module"][src]')).find(node => new URL(node.getAttribute("src"), request.url).pathname.endsWith("/assets/collection-infinite-scroll.js"));
      if (loaderDocument) {
        if (!script) throw new Error("Collection loader definition is unavailable");
        await import(new URL(script.getAttribute("src"), request.url).href);
        this.assertCurrent(request);
      }
    }
  }

  waitForStylesheet(link, signal) {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        link.removeEventListener("load", loaded);
        link.removeEventListener("error", failed);
        signal.removeEventListener("abort", aborted);
      };
      const loaded = () => { cleanup(); resolve(); };
      const failed = () => { cleanup(); reject(new Error("Collection stylesheet failed to load")); };
      const aborted = () => { cleanup(); reject(new DOMException("Collection stylesheet cancelled", "AbortError")); };
      link.addEventListener("load", loaded, { once: true });
      link.addEventListener("error", failed, { once: true });
      signal.addEventListener("abort", aborted, { once: true });
      if (signal.aborted) aborted();
    });
  }

  updateHead(metadata) {
    document.title = metadata.dataset.title;
    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = metadata.dataset.canonical;
    let description = document.head.querySelector('meta[name="description"]');
    if (metadata.dataset.description) {
      if (!description) {
        description = document.createElement("meta");
        description.name = "description";
        document.head.append(description);
      }
      description.content = metadata.dataset.description;
    } else description?.remove();
    const incomingHead = metadata.content.querySelector("[data-collection-navigation-head]");
    document.head.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"], script[data-theme-microdata]').forEach(node => node.remove());
    incomingHead?.querySelectorAll('meta, script[type="application/ld+json"][data-theme-microdata]').forEach(node => document.head.append(document.importNode(node, true)));
  }

  finish(request, success) {
    if (this.pending !== request) return;
    clearTimeout(request.timeout);
    this.clearLoading(request);
    this.pending = null;
    this.main.removeAttribute("aria-busy");
    this.main.removeAttribute("data-collection-navigation-commit");
    history.scrollRestoration = request.scrollRestoration;
    document.dispatchEvent(new CustomEvent("collection:navigation:complete", { detail: {
      sectionId: request.sectionId, success, committed: request.committed === true, url: request.url.href,
      fromUrl: request.fromUrl, historyNavigation: request.historyNavigation,
      collection: request.collection, error: request.error
    } }));
  }

  fallback(url, replace) {
    if (replace) location.replace(url.href);
    else location.assign(url.href);
  }
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => new CollectionNavigation(), { once: true });
else new CollectionNavigation();
// END CUSTOM EDIT
